import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, UserPlus, Mail, Phone, Shield, Truck, ShoppingCart,
  RefreshCw, MoreVertical, Check, X, Clock, AlertCircle,
  Edit, Trash2, Send, ChevronDown, User, Briefcase
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Role Icons & Colors
const ROLE_CONFIG = {
  admin: { icon: Shield, color: 'text-red-400', bgColor: 'bg-red-500/20', borderColor: 'border-red-500/30' },
  manager: { icon: Briefcase, color: 'text-blue-400', bgColor: 'bg-blue-500/20', borderColor: 'border-blue-500/30' },
  cashier: { icon: ShoppingCart, color: 'text-green-400', bgColor: 'bg-green-500/20', borderColor: 'border-green-500/30' },
  driver: { icon: Truck, color: 'text-purple-400', bgColor: 'bg-purple-500/20', borderColor: 'border-purple-500/30' }
};

const STATUS_CONFIG = {
  active: { label: 'Aktiv', color: 'text-green-400', bgColor: 'bg-green-500/20' },
  invited: { label: 'Eingeladen', color: 'text-yellow-400', bgColor: 'bg-yellow-500/20' },
  disabled: { label: 'Deaktiviert', color: 'text-gray-400', bgColor: 'bg-gray-500/20' },
  removed: { label: 'Entfernt', color: 'text-red-400', bgColor: 'bg-red-500/20' }
};

export default function TeamManagement() {
  // State
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  
  // Invite Form
  const [inviteForm, setInviteForm] = useState({
    email: '',
    name: '',
    role: 'driver',
    phone: ''
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  // ==========================================================================
  // API Functions
  // ==========================================================================

  const loadMembers = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/team/members`, {
        headers: getAuthHeaders()
      });
      
      if (response.status === 401) {
        toast.error('Bitte erneut anmelden');
        return;
      }
      
      const data = await response.json();
      setMembers(data.members || []);
    } catch (error) {
      console.error('Error loading members:', error);
      toast.error('Fehler beim Laden der Teammitglieder');
    }
  }, []);

  const loadRoles = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/team/roles`);
      const data = await response.json();
      setRoles(data.roles || []);
    } catch (error) {
      console.error('Error loading roles:', error);
    }
  }, []);

  const inviteMember = async () => {
    if (!inviteForm.email || !inviteForm.name) {
      toast.error('Bitte E-Mail und Name angeben');
      return;
    }
    
    setInviteLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/team/invite`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(inviteForm)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Einladung fehlgeschlagen');
      }
      
      toast.success(data.message);
      setShowInviteDialog(false);
      setInviteForm({ email: '', name: '', role: 'driver', phone: '' });
      loadMembers();
      
      // Zeige Einladungslink (für Demo)
      if (data.member?.invite_link) {
        toast.info(`Einladungslink: ${data.member.invite_link}`, { duration: 10000 });
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setInviteLoading(false);
    }
  };

  const toggleMemberStatus = async (member) => {
    try {
      const response = await fetch(`${API_BASE}/api/team/members/${member.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ active: !member.active })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Fehler');
      }
      
      toast.success(member.active ? 'Mitarbeiter deaktiviert' : 'Mitarbeiter aktiviert');
      loadMembers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const deleteMember = async () => {
    if (!selectedMember) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/team/members/${selectedMember.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Fehler');
      }
      
      toast.success(data.message);
      setShowDeleteDialog(false);
      setSelectedMember(null);
      loadMembers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const resendInvite = async (member) => {
    try {
      const response = await fetch(`${API_BASE}/api/team/members/${member.id}/resend-invite`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Fehler');
      }
      
      toast.success(data.message);
      
      if (data.invite_link) {
        toast.info(`Neuer Link: ${data.invite_link}`, { duration: 10000 });
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Initial load
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([loadMembers(), loadRoles()]);
      setLoading(false);
    };
    load();
  }, [loadMembers, loadRoles]);

  // ==========================================================================
  // Render Helpers
  // ==========================================================================

  const getRoleIcon = (role) => {
    const config = ROLE_CONFIG[role] || ROLE_CONFIG.cashier;
    const Icon = config.icon;
    return <Icon className={`w-4 h-4 ${config.color}`} />;
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.active;
    return (
      <Badge className={`${config.bgColor} ${config.color} border-0`}>
        {status === 'active' && <span className="w-2 h-2 bg-green-400 rounded-full mr-1.5 animate-pulse" />}
        {status === 'invited' && <Clock className="w-3 h-3 mr-1" />}
        {config.label}
      </Badge>
    );
  };

  // Stats
  const stats = {
    total: members.length,
    active: members.filter(m => m.status === 'active').length,
    invited: members.filter(m => m.status === 'invited').length,
    drivers: members.filter(m => m.role === 'driver').length
  };

  // ==========================================================================
  // Render
  // ==========================================================================

  return (
    <div className="p-6 space-y-6" data-testid="team-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Users className="w-8 h-8 text-purple-400" />
            Team-Verwaltung
          </h1>
          <p className="text-gray-400 mt-1">Mitarbeiter einladen und verwalten</p>
        </div>
        
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={loadMembers}
            className="border-gray-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Aktualisieren
          </Button>
          
          <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
            <DialogTrigger asChild>
              <Button className="bg-purple-600 hover:bg-purple-700" data-testid="invite-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Mitarbeiter einladen
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-purple-400" />
                  Mitarbeiter einladen
                </DialogTitle>
                <DialogDescription>
                  Der Mitarbeiter erhält eine E-Mail mit Einladungslink.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>E-Mail *</Label>
                  <Input
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})}
                    placeholder="max@restaurant.de"
                    className="bg-gray-800 border-gray-700"
                    data-testid="invite-email-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input
                    value={inviteForm.name}
                    onChange={(e) => setInviteForm({...inviteForm, name: e.target.value})}
                    placeholder="Max Mustermann"
                    className="bg-gray-800 border-gray-700"
                    data-testid="invite-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Rolle *</Label>
                  <Select
                    value={inviteForm.role}
                    onValueChange={(value) => setInviteForm({...inviteForm, role: value})}
                  >
                    <SelectTrigger className="bg-gray-800 border-gray-700" data-testid="invite-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map((role) => (
                        <SelectItem key={role.id} value={role.id}>
                          <div className="flex items-center gap-2">
                            {getRoleIcon(role.id)}
                            <span>{role.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {inviteForm.role === 'driver' && (
                    <p className="text-xs text-purple-400 flex items-center gap-1">
                      <Truck className="w-3 h-3" />
                      Fahrer können sich in der Flotto App anmelden
                    </p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label>Telefon (optional)</Label>
                  <Input
                    value={inviteForm.phone}
                    onChange={(e) => setInviteForm({...inviteForm, phone: e.target.value})}
                    placeholder="+49 123 456789"
                    className="bg-gray-800 border-gray-700"
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
                  Abbrechen
                </Button>
                <Button
                  onClick={inviteMember}
                  disabled={inviteLoading || !inviteForm.email || !inviteForm.name}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="send-invite-btn"
                >
                  {inviteLoading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Einladung senden
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Gesamt</p>
                <p className="text-3xl font-bold text-white">{stats.total}</p>
              </div>
              <Users className="w-8 h-8 text-gray-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Aktiv</p>
                <p className="text-3xl font-bold text-green-400">{stats.active}</p>
              </div>
              <Check className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Eingeladen</p>
                <p className="text-3xl font-bold text-yellow-400">{stats.invited}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Fahrer</p>
                <p className="text-3xl font-bold text-purple-400">{stats.drivers}</p>
              </div>
              <Truck className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Members Table */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white">Teammitglieder</CardTitle>
          <CardDescription>Alle Mitarbeiter Ihres Restaurants</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-purple-500" />
              <p className="text-gray-400 mt-2">Laden...</p>
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400 text-lg">Noch keine Teammitglieder</p>
              <p className="text-gray-500 text-sm">Laden Sie Mitarbeiter ein um loszulegen</p>
              <Button
                onClick={() => setShowInviteDialog(true)}
                className="mt-4 bg-purple-600 hover:bg-purple-700"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Ersten Mitarbeiter einladen
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-gray-800">
                  <TableHead className="text-gray-400">Mitarbeiter</TableHead>
                  <TableHead className="text-gray-400">Rolle</TableHead>
                  <TableHead className="text-gray-400">Status</TableHead>
                  <TableHead className="text-gray-400">Kontakt</TableHead>
                  <TableHead className="text-gray-400">Hinzugefügt</TableHead>
                  <TableHead className="text-gray-400 text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.filter(m => m.status !== 'removed').map((member) => {
                  const roleConfig = ROLE_CONFIG[member.role] || ROLE_CONFIG.cashier;
                  const RoleIcon = roleConfig.icon;
                  
                  return (
                    <TableRow key={member.id} className="border-gray-800">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full ${roleConfig.bgColor} flex items-center justify-center`}>
                            <User className={`w-5 h-5 ${roleConfig.color}`} />
                          </div>
                          <div>
                            <p className="text-white font-medium">{member.name}</p>
                            <p className="text-gray-500 text-sm">{member.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${roleConfig.bgColor} ${roleConfig.color} ${roleConfig.borderColor} border`}>
                          <RoleIcon className="w-3 h-3 mr-1" />
                          {member.role_label}
                        </Badge>
                        {member.driver_id && (
                          <p className="text-xs text-gray-500 mt-1">ID: {member.driver_id}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(member.status)}
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <p className="text-gray-300 text-sm flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {member.email}
                          </p>
                          {member.phone && (
                            <p className="text-gray-400 text-sm flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {member.phone}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-400 text-sm">
                        {member.created_at ? new Date(member.created_at).toLocaleDateString('de-DE') : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {member.status === 'invited' && (
                              <DropdownMenuItem onClick={() => resendInvite(member)}>
                                <Send className="w-4 h-4 mr-2" />
                                Einladung erneut senden
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => toggleMemberStatus(member)}>
                              {member.active ? (
                                <><X className="w-4 h-4 mr-2" />Deaktivieren</>
                              ) : (
                                <><Check className="w-4 h-4 mr-2" />Aktivieren</>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-red-400"
                              onClick={() => {
                                setSelectedMember(member);
                                setShowDeleteDialog(true);
                              }}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Entfernen
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-purple-500/10 border-purple-500/30">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <h3 className="text-white font-medium mb-1">Einheitliches Login-System</h3>
              <p className="text-gray-400 text-sm">
                Alle Mitarbeiter melden sich mit ihrer E-Mail-Adresse an. 
                <strong className="text-purple-400"> Fahrer</strong> werden automatisch zur Flotto App weitergeleitet,
                während andere Rollen das Cash-X Dashboard sehen.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mitarbeiter entfernen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie <strong>{selectedMember?.name}</strong> wirklich entfernen?
              Der Zugang wird sofort gesperrt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteMember}
              className="bg-red-600 hover:bg-red-700"
            >
              Entfernen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
